'use client';

import { useEffect } from 'react';

const PerformanceOptimizer = () => {
  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    // Preload critical resources (client-side only) - optimized
    const preloadCriticalResources = () => {
      // Preload hero image (client-side)
      const heroImage = new Image();
      heroImage.src = '/images/ifueltax-logo.png';

      // Preload critical fonts (client-side) - only if not already loaded
      if (!document.querySelector('link[href*="inter/v12"]')) {
        const fontLink = document.createElement('link');
        fontLink.rel = 'preload';
        fontLink.href = 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2';
        fontLink.as = 'font';
        fontLink.type = 'font/woff2';
        fontLink.crossOrigin = 'anonymous';
        document.head.appendChild(fontLink);
      }
    };

    // Intersection Observer for lazy loading - optimized
    const setupLazyLoading = () => {
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              observer.unobserve(img);
            }
          });
        }, {
          rootMargin: '50px 0px', // Start loading 50px before element enters viewport
          threshold: 0.1
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    };

    // Service Worker registration for caching - optimized
    const registerServiceWorker = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js');
          console.log('Service Worker registered:', registration);
        } catch (error) {
          console.log('Service Worker registration failed:', error);
        }
      }
    };

    // Preconnect to external domains (client-side) - optimized
    const preconnectToExternalDomains = () => {
      const domains = [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
        'https://www.google-analytics.com',
        'https://www.googletagmanager.com'
      ];

      domains.forEach(domain => {
        // Only add if not already present
        if (!document.querySelector(`link[href="${domain}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preconnect';
          link.href = domain;
          link.crossOrigin = 'anonymous';
          document.head.appendChild(link);
        }
      });
    };

    // DNS prefetch for performance (client-side) - optimized
    const dnsPrefetch = () => {
      const domains = [
        '//fonts.googleapis.com',
        '//fonts.gstatic.com',
        '//www.google-analytics.com',
        '//www.googletagmanager.com'
      ];

      domains.forEach(domain => {
        // Only add if not already present
        if (!document.querySelector(`link[href="${domain}"]`)) {
          const link = document.createElement('link');
          link.rel = 'dns-prefetch';
          link.href = domain;
          document.head.appendChild(link);
        }
      });
    };

    // Execute optimizations with delay to prioritize critical rendering
    const executeOptimizations = () => {
      // Immediate optimizations
      preloadCriticalResources();
      
      // Delayed optimizations to not block initial render
      setTimeout(() => {
        setupLazyLoading();
        preconnectToExternalDomains();
        dnsPrefetch();
      }, 100);
      
      // Service worker registration after page load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', registerServiceWorker);
      } else {
        registerServiceWorker();
      }
    };

    executeOptimizations();

    // Cleanup function
    return () => {
      // Cleanup if needed
    };
  }, []);

  return null; // This component doesn't render anything, its static parts moved to layout.tsx
};

export default PerformanceOptimizer;
